// pages/ModulesPage.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Boxes,
  Plus,
  Search,
  Pencil,
  Eye,
  Layers3,
  Trash2,
  ArrowLeft,
} from "lucide-react";

import { useDispatch, useSelector } from "react-redux";

import ColumnVisibilityDropdown from "@/components/common/ColumnVisibilityDropdown";
import { DataTable } from "@/components/ui/table";
import TablePagination from "@/components/common/TablePagination";
import TableText from "@/components/common/TableText";

// import your redux action
import { useNavigate, useParams } from "react-router-dom";
import AddModuleModal from "../modals/AddModuleModal";
import { deleteModule, getModules } from "@/redux/Modules/modules/actions";
import ConfirmModal from "@/components/ui/CelebrationButton/ConfirmModal/ConfirmModal";
import { showToast } from "@/redux/Toast/actions";
import { getBaseModuleDropdown } from "@/redux/Modules/baseModules/actions";
import { SelectInput } from "../components/FormControls";

const ModulesPage = () => {
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const { baseModuleId } = useParams();

  const { loading, modulesList, pagination } = useSelector(
    (state) => state.modules,
  );
  console.log("modulesList: ", modulesList);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  console.log("selectedModule: ", selectedModule);

  // ======================================================
  // COLUMNS
  // ======================================================

  const columns = [
    {
      key: "MODULE_NAME",
      label: "Module",
      width: 320,
      sticky: "left",

      render: (row) => (
        <div
          onClick={() => handleView(row)}
          className="
        flex items-center gap-3 min-w-0
        cursor-pointer
        rounded-xl
        p-2
        -m-2
        transition-all duration-200
        hover:bg-slate-50
        dark:hover:bg-slate-800
      "
        >
          <div className="w-10 h-10 shrink-0 rounded-xl bg-teal-500/10 flex items-center justify-center">
            <Boxes className="w-5 h-5 text-teal-500" />
          </div>

          <div className="min-w-0 flex-1">
            <TableText
              title={row.MODULE_NAME}
              subtitle={row.MODULE_DESCRIPTION || "No description"}
              multiline
              maxLines={2}
            />
          </div>
        </div>
      ),
    },

    {
      key: "BASEMODULE_NAME",
      label: "Base Module",
      width: 220,

      render: (row) => (
        <div className="flex items-center gap-2">
          <Layers3 className="w-4 h-4 text-blue-500 shrink-0" />

          <span className="font-medium text-slate-700 dark:text-slate-300">
            {row.BASEMODULE_NAME}
          </span>
        </div>
      ),
    },

    {
      key: "MODULE_BASEPRICE",
      label: "Base Price",
      width: 140,

      render: (row) => (
        <span className="font-semibold text-slate-900 dark:text-white">
          ${row.MODULE_BASEPRICE}
        </span>
      ),
    },

    {
      key: "MODULE_BASEPRICETYPE",
      label: "Pricing Type",
      width: 160,

      render: (row) => (
        <span className="text-slate-700 dark:text-slate-300">
          {row.MODULE_BASEPRICETYPE}
        </span>
      ),
    },

    {
      key: "MODULE_PERSEATPRICE",
      label: "Per Seat",
      width: 120,

      render: (row) => (
        <span className="text-slate-700 dark:text-slate-300">
          ${row.MODULE_PERSEATPRICE}
        </span>
      ),
    },

    {
      key: "MODULE_USAGERATE",
      label: "Usage Rate",
      width: 120,

      render: (row) => (
        <span className="text-slate-700 dark:text-slate-300">
          ${row.MODULE_USAGERATE}
        </span>
      ),
    },

    {
      key: "MODULE_TRIAL_DAYS",
      label: "Trial",
      width: 100,

      render: (row) => (
        <span className="text-slate-700 dark:text-slate-300">
          {row.MODULE_TRIAL_DAYS} Days
        </span>
      ),
    },

    {
      key: "SUBMODULES_DATA",
      label: "Submodules",
      width: 120,

      render: (row) => (
        <span className="font-medium text-slate-900 dark:text-white">
          {row?.SUBMODULES_DATA?.length || 0}
        </span>
      ),
    },

    {
      key: "MODULE_IS_ACTIVE",
      label: "Status",
      width: 120,

      render: (row) => (
        <span
          className={`px-2.5 py-1 rounded-full text-xs font-medium ${
            row.MODULE_IS_ACTIVE
              ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
              : "bg-red-500/10 text-red-600 dark:text-red-400"
          }`}
        >
          {row.MODULE_IS_ACTIVE ? "Active" : "Inactive"}
        </span>
      ),
    },

    {
      key: "actions",
      label: "Actions",
      width: 140,
      sticky: "right",

      render: (row) => (
        <div className="flex items-center gap-2">
          {/* View */}

          <button
            onClick={() => handleView(row)}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            title="View"
          >
            <Eye className="w-4 h-4 text-slate-600 dark:text-slate-300" />
          </button>

          {/* Edit */}

          <button
            onClick={() => handleEdit(row)}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            title="Edit"
          >
            <Pencil className="w-4 h-4 text-blue-500" />
          </button>

          {/* Toggle Status */}

          <button
            onClick={() => handleDeleteClick(row)}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            title="Toggle Status"
          >
            <Trash2 className="w-4 h-4 text-amber-500" />
          </button>
        </div>
      ),
    },
  ];

  const [visibleColumns, setVisibleColumns] = useState(() =>
    columns.map((col) => col.key),
  );

  const [currentPage, setCurrentPage] = useState(1);

  const [pageSize, setPageSize] = useState(10);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [baseModulesDropdown, setBaseModulesDropdown] = useState([]);

  // ======================================================
  // FILTERED COLUMNS
  // ======================================================

  const filteredColumns = columns.filter(
    (column) =>
      visibleColumns.length === 0 || visibleColumns.includes(column.key),
  );

  // ======================================================
  // FETCH
  // ======================================================

  const fetchModules = useCallback(() => {
    const requestPayload = {
      iPageNo: currentPage,
      iPageRecords: pageSize,
      SearchKeyword: debouncedSearch,
      BaseModuleId: Number(baseModuleId || 0),
    };

    dispatch(getModules(requestPayload));
  }, [dispatch, currentPage, pageSize, debouncedSearch, baseModuleId]);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchTerm.trim();

      if (trimmed === "" || trimmed.length >= 2) {
        setDebouncedSearch(trimmed);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const loadDropdown = async () => {
      try {
        const res = await dispatch(getBaseModuleDropdown());
        setBaseModulesDropdown(res);
      } catch (err) {
        console.error(err);
      }
    };

    loadDropdown();
  }, []);

  // ======================================================
  // HANDLERS
  // ======================================================

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleAddModule = () => {
    setSelectedModule(null);
    setIsEdit(false);
    setOpenModal(true);
  };

  const handleView = (row) => {
    navigate(
      `/admin/base_modules/${row.BASEMODULE_ID}/modules/${row.MODULE_ID}/sub_modules`,
      {
        state: {
          baseModuleName: row.MODULE_ID,
        },
      },
    );
  };

  const handleEdit = (row) => {
    setSelectedModule(row);
    setIsEdit(true);
    setOpenModal(true);
  };

  const handleDeleteClick = (row) => {
    setSelectedModule(row);
    setConfirmOpen(true);
  };

  const handleConfirmAction = async () => {
    try {
      const payload = {
        MODULE_ID: selectedModule?.MODULE_ID,
        DELETED_BY: "admin", // replace with logged-in user
      };

      await dispatch(deleteModule(payload));
      fetchModules();
      dispatch(showToast("Module deleted successfully", "success", true));

      setConfirmOpen(false);

      // refresh list if needed
      // dispatch(getSubModules(...));
    } catch (error) {
      dispatch(
        showToast(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to delete Module",
          "error",
          true,
        ),
      );
    }
  };

  // ======================================================
  // RETURN
  // ======================================================

  return (
    <>
      <div className="min-h-screen bg-slate-100 dark:bg-slate-900 p-6">
        <div className="space-y-4">
          {/* ================================================= */}
          {/* HEADER */}
          {/* ================================================= */}

          <div className="flex items-center justify-between">
            <div className="flex items-start gap-3">
              <button
                onClick={() => navigate("/admin/base_modules")}
                className="mt-1 p-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                aria-label="Go Back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>

              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <Boxes className="w-7 h-7 text-teal-500" />
                  Modules
                </h1>

                <p className="text-slate-500 dark:text-slate-400 mt-1">
                  Manage modules, pricing and configurations
                </p>
              </div>
            </div>

            <button
              onClick={handleAddModule}
              className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Module
            </button>
          </div>

          {/* ================================================= */}
          {/* SEARCH */}
          {/* ================================================= */}

          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="relative w-full md:w-96">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                <input
                  type="text"
                  placeholder="Search module..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
                <span className="font-bold text-slate-900 dark:text-white">
                  {pagination?.totalRecord ?? 0} module found
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <SelectInput
                name="BASEMODULE_ID"
                value={baseModuleId}
                onValueChange={(name, value) => {
                  navigate(`/admin/base_modules/${value}/modules`);
                }}
                placeholder="Select Base Module"
                className="!py-2"
                options={[
                  { value: "0", label: "All Base Modules" }, // important
                  ...baseModulesDropdown.map((item) => ({
                    value: item.VALUE,
                    label: item.TEXT,
                  })),
                ]}
              />
              <ColumnVisibilityDropdown
                columns={columns}
                visibleColumns={visibleColumns}
                setVisibleColumns={setVisibleColumns}
                storageKey="modules-table-columns"
              />
            </div>
          </div>

          {/* ================================================= */}
          {/* TABLE */}
          {/* ================================================= */}

          <DataTable
            data={modulesList}
            columns={filteredColumns}
            loading={loading}
            emptyText="No Modules found"
            Icon={Boxes}
          />

          {/* ================================================= */}
          {/* PAGINATION */}
          {/* ================================================= */}

          <div className="p-4">
            <TablePagination
              currentPage={currentPage}
              totalPages={pagination?.totalPage || 1}
              totalRecords={pagination?.totalRecord || 0}
              currentRecord={pagination?.currentRecord || 0}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        </div>
      </div>

      <AddModuleModal
        open={openModal}
        onOpenChange={setOpenModal}
        isEdit={isEdit}
        editData={selectedModule}
        onSuccess={fetchModules}
        baseModulesDropdown={baseModulesDropdown}
      />

      <ConfirmModal
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirmAction}
        confirmText="Delete"
        title="Delete Module"
        message={
          <>
            Are you sure you want to delete{" "}
            <span className="font-bold text-slate-900 dark:text-white">
              {selectedModule?.MODULE_NAME} ?
            </span>
          </>
        }
      />
    </>
  );
};

export default ModulesPage;
